import React from "react";

import { ArrayFieldState } from "../../../types";

import { getErrorMessagesFromZodErrors } from "../../../utils";

import { InputWrapper, InputWrapperProps } from "../../../../../molecules";
import { usePyForm } from "../../../context";

export type BaseFormListProps<T> = InputWrapperProps & {
	input: (field: ArrayFieldState<T>) => React.ReactNode;
};

/**
 * This is a Base Form Field component that wraps an input and prepares it for the context within Form
 * It supplies an input wrapper that is already hooked into the form context and has the correct props
 * You should ALWAYS try to use one of the existing Form input components, which exist for each input type by prepending Form to the input component name
 * This component is ultimately a fallback for composite inputs, arrays, unique inputs within forms
 * @param input - The input component to wrap
 * @param props - Input Wrapper Props crosscut with some form field props
 * @returns The wrapped input component
 */
export const FormList = <T,>({ input, ...props }: BaseFormListProps<T>) => {
	const form = usePyForm();
	return (
		<form.Field<T[]> name={props.id} mode="array">
			{(field) => {
				// get the errors from meta and serverError and format them for display
				const errorsMeta: { message: string }[] =
					(field.state.meta.errors as unknown as { message: string }[]) || [];
				const errors = field.state?.meta?.serverError || getErrorMessagesFromZodErrors(errorsMeta);

				const value = field.state.value;
				const setValue = field.handleChange;
				const touched = field.state.meta.isTouched;
				const isDirty = field.state.meta.isDirty;
				const hasErrors = errors && errors.length > 0;

				// here we pass the field state to the input component so that the inner child can run a callback with the relevant field state
				return (
					<InputWrapper
						{...props}
						id={field.name}
						label={props.label}
						error={hasErrors && (touched || isDirty) ? errors : undefined}
						group
					>
						{input({
							value: value,
							onChange: setValue,
							onBlur: field.handleBlur,
							errors: errorsMeta,
							// tanstack forms has the value of data set as an array. However pushing an array here leads to [...array, [data]], rather than [...array, data].
							// Therefore we want to override Tanstack Forms pushValue Type to be just T to better align this with how we handle the type (T and let the component handle the Array), and set the type at this point just before it goes to tanstack's library
							pushValue: (data: T) => field.pushValue(data as T[]),
							insertValue: (index: number, data: T) => {
								const newList = [...field.state.value];
								newList.splice(index, 0, data);
								field.handleChange(newList);
							},
							shiftValue: (oldIndex: number, newIndex: number) => {
								const newList = [...field.state.value];
								const [movedItem] = newList.splice(oldIndex, 1);
								newList.splice(newIndex, 0, movedItem);
								field.handleChange(newList);
								return newList;
							},
							removeValue: field.removeValue,
							swapValues: field.swapValues,
							// tanstack forms has the value of data set as an array. However pushing an array here leads to [...array, [data]], rather than [...array, data].
							// Therefore we want to override Tanstack Forms pushValue Type to be just T to better align this with how we handle the type (T and let the component handle the Array), and set the type at this point just before it goes to tanstack's library
							replaceValue: (index: number, value: T) => field.replaceValue(index, value as T[]),
						})}
					</InputWrapper>
				);
			}}
		</form.Field>
	);
};

FormList.displayName = "FormList";
